<ng-container *ngIf="request$ | async as request; else editSkeleton">
    <div class="edit-page">
        <div class="container edit-container" [ngClass]="{'cutter-expanded': cutterMappingActive}">
            <kendo-card class="edit-card" style="width: 100%; padding-top: 5px;">
                <kendo-card-body>
                    <div class="edit-header">
                        <div class="edit-title">
                            <span class="edit-title-label">{{ isNewMode ? 'New' : 'Request' }}</span>
                            <span class="edit-title-value">{{ isNewMode ? 'Request' : id }}</span>
                        </div>
                        <div class="edit-status">
                            <kendo-badge-container style="margin-right: 10px;">
                                <!-- New mode: Show Draft badge -->
                                <kendo-badge themeColor="info" shape="pill" [align]="align" rounded="full"
                                    [position]="position" *ngIf="isNewMode">Draft</kendo-badge>
                                <!-- Edit mode: Show status badges -->
                                <kendo-badge [themeColor]="orderLocked ? 'primary' : 'warning'" shape="pill"
                                    [align]="align" rounded="full" [position]="position"
                                    *ngIf="!isNewMode && !orderCompleted">{{orderLocked ? "in Process" :
                                    "Pending Pickup"}}</kendo-badge>
                                <kendo-badge themeColor="success" shape="pill" [align]="align" [position]="position"
                                    rounded="full" *ngIf="!isNewMode && orderCompleted">Complete</kendo-badge>
                            </kendo-badge-container>
                            <span class="status-icon locked k-icon k-font-icon k-i-lock" [title]="lockReason"
                                *ngIf="!isNewMode && (orderLocked || orderCompleted)"></span>
                            <span class="status-icon unlocked k-icon k-font-icon k-i-unlock"
                                title="Editing is allowed until the RTA is picked up"
                                *ngIf="!isNewMode && !orderLocked && !orderCompleted"></span>
                            <span class="status-icon k-icon k-font-icon k-i-edit"
                                title="New request - fill in details and save" *ngIf="isNewMode"></span>
                        </div>
                    </div>
                    @if(lockReason) {
                    <div class="lock-banner">
                        <span class="k-icon k-font-icon k-i-warning"></span>
                        <span>{{lockReason}}</span>
                    </div>
                    }
                    @if(isApprovalTask) {
                    <div class="approval-banner" [class.approval-banner-readonly]="!canApprove">
                        <div class="approval-banner-content">
                            <div class="approval-info">
                                <span class="k-icon k-font-icon approval-icon" [ngClass]="canApprove ? 'k-i-check-circle' : 'k-i-lock'"></span>
                                <div class="approval-text">
                                    <strong>{{approvalTaskName}}</strong>
                                    @if(canApprove) {
                                    <span>This request requires your approval to proceed.</span>
                                    } @else {
                                    <span>This request is pending approval. You do not have permission to approve or reject.</span>
                                    }
                                </div>
                            </div>
                            @if(canApprove) {
                            <div class="approval-actions">
                                <button kendoButton themeColor="success" fillMode="solid" (click)="onApprove()" [disabled]="isApproving || isRejecting">
                                    @if(isApproving) {
                                    <kendo-loader size="small" themeColor="light"></kendo-loader>
                                    <span style="margin-left: 6px;">Approving...</span>
                                    } @else {
                                    <span class="k-icon k-font-icon k-i-check"></span>
                                    <span>Approve</span>
                                    }
                                </button>
                                <button kendoButton themeColor="error" fillMode="solid" (click)="onReject()" [disabled]="isApproving || isRejecting">
                                    <span class="k-icon k-font-icon k-i-close"></span>
                                    <span>Reject</span>
                                </button>
                            </div>
                            }
                        </div>
                    </div>
                    }
                    <div class="edit-meta">
                        <div class="meta-item">
                            <span class="meta-label">Requestor</span>
                            <span class="meta-value">{{ isNewMode ? 'You' : (request?.submittedByUser?.displayName ??
                                '') }}</span>
                        </div>
                        <div class="meta-item">
                            <span class="meta-label">Status</span>
                            <span class="meta-value">{{ isNewMode ? 'New Draft' : (request.process?.name ?? '')
                                }}</span>
                        </div>
                    </div>
                    <kendo-tabstrip class="edit-tabstrip" [tabPosition]="'top'" [keepTabContent]="true"
                        (tabSelect)="onTabSelect($event)">
                        <kendo-tabstrip-tab title="Basic Data" [selected]="true">
                            <ng-template kendoTabContent>
                                <app-basic-data [draftId]="isNewMode ? undefined : id" [initial]="request"
                                    [readonly]="orderLocked || orderCompleted"
                                    (productLineChanged)="onProductlineChanged($event)"></app-basic-data>
                            </ng-template>
                        </kendo-tabstrip-tab>
                        <kendo-tabstrip-tab title="Additional Data" [disabled]="isNewMode">
                            <ng-template kendoTabContent>
                                @if(!isNewMode) {
                                <app-fixedcutter-data [draftId]="id" [bitInitial]="request.bit"
                                    [readonly]="orderLocked || orderCompleted" (sizeChange)="onSizeChanged($event)"
                                    [excluded]="request.requestExclusion || false" [ProductLine$]="activeProductLine$"
                                    (nomenclaturePendingChanged)="onNomenclaturePendingChanged($event)"
                                    *ngIf="(activeSubPSL$ | async) === 'Fixed Cutter'"></app-fixedcutter-data>
                                <app-powderbinder-data [draftId]="id" [powderInitial]="request.powders"
                                    [readonly]="orderLocked || orderCompleted"
                                    *ngIf="(activeProductLine$ | async) === 'Matrix'"
                                    [Size]="Size$"></app-powderbinder-data>
                                <app-gaugpadprotection-data [draftId]="id" [productLineId]="request.productLine"
                                    [readonly]="orderLocked || orderCompleted"
                                    [protectionInitial]="request.gaugeProtections"
                                    *ngIf="(activeSubPSL$ | async) === 'Fixed Cutter'"></app-gaugpadprotection-data>
                                <app-arm-data [draftId]="id" [armInitial]="request.arm"
                                    [readonly]="orderLocked || orderCompleted"
                                    [ProductLine$]="activeProductLine$"
                                    *ngIf="(activeSubPSL$ | async) === 'DHT'"></app-arm-data>    
                                <app-rollercone-data [draftId]="id" [rollerConeInitial]="request.rollerCone"
                                    [readonly]="orderLocked || orderCompleted"
                                    *ngIf="(activeSubPSL$ | async) === 'Roller Cone'"></app-rollercone-data>
                                <app-bitfeature-data [draftId]="id" [preselectedIds]="getBitFeatureIds(request)"
                                    [readonly]="orderLocked || orderCompleted">
                                </app-bitfeature-data>
                                } @else {
                                <div class="new-mode-placeholder">
                                    <span class="k-icon k-font-icon k-i-info"></span>
                                    <p>Save your request first to access Additional Data options.</p>
                                </div>
                                }
                            </ng-template>
                        </kendo-tabstrip-tab>
                        <kendo-tabstrip-tab title="Cutter Mapping" [disabled]="isNewMode" *ngIf="(activeSubPSL$ | async) === 'Fixed Cutter' || (activeSubPSL$ | async) === 'DHT'">
                            <ng-template kendoTabContent>
                                @if(!isNewMode) {
                                <app-cuttermapping-data [cutterMappingInitial]="request.orD_CutterMap || []"
                                    [retrofit]="request.requestExclusion || false" [isEJDF]="false" [draftId]="id"
                                    [readonly]="orderLocked || orderCompleted"></app-cuttermapping-data>
                                } @else {
                                <div class="new-mode-placeholder">
                                    <span class="k-icon k-font-icon k-i-info"></span>
                                    <p>Save your request first to access Cutter Mapping.</p>
                                </div>
                                }
                            </ng-template>
                        </kendo-tabstrip-tab>
                        <kendo-tabstrip-tab title="Files" [disabled]="isNewMode">
                            <ng-template kendoTabContent>
                                @if(!isNewMode) {
                                <div style="margin-bottom: 15px;">
                                    <fv-file-upload [targetId]="id || 0" [applicationTypeId]="4" (uploadCompleted)="onUploadCompleted($event)"></fv-file-upload>
                                </div>
                                <div>
                                    <fv-file-list [initialCriteria]="{ targetId: id, includeSubdirectories: true }"
                                        [showToolbar]="false" [applicationTypeId]="4" [targetId]="id || 0"
                                        [visibleApplicationTypeNames]="['Ejdf']" [showCheckout]="false" [refreshToken]="fileListRefreshToken"></fv-file-list>
                                </div>
                                } @else {
                                <div class="new-mode-placeholder">
                                    <span class="k-icon k-font-icon k-i-info"></span>
                                    <p>Save your request first to upload files.</p>
                                </div>
                                }
                            </ng-template>
                        </kendo-tabstrip-tab>
                        <kendo-tabstrip-tab title="History" [disabled]="isNewMode">
                            <ng-template kendoTabContent>
                                @if(!isNewMode) {
                                <app-history-data [history]="request.history"></app-history-data>
                                } @else {
                                <div class="new-mode-placeholder">
                                    <span class="k-icon k-font-icon k-i-info"></span>
                                    <p>No history yet - this is a new request.</p>
                                </div>
                                }
                            </ng-template>
                        </kendo-tabstrip-tab>
                    </kendo-tabstrip>
                </kendo-card-body>
            </kendo-card>
            <kendo-floatingactionbutton class="edit-fab" [positionMode]="'fixed'" text="Action" title="Action"
                [dialItems]="actionItems"
                (dialItemClick)="onDialItemClick($event)" #anchor>
            </kendo-floatingactionbutton>
            @if (viewChat) {
            <kendo-window [resizable]="false" [(left)]="windowLeft" [minWidth]="300" [minHeight]="500"
                title="RTA/EJDF Communications" [keepContent]="true" autoFocusedElement="#messageBoxInput"
                (close)="viewChat=false">
                <app-communication-data [_initialcommunications]="request.communication"
                    [draftId]="id"></app-communication-data>
            </kendo-window>}
            @if(viewSubmit){
            <kendo-dialog title="Please confirm before submit" (close)="onCancelSubmit()" [minWidth]="250" [width]="800"
                class="submit-dialog">
                <div class="submit-dialog-content">
                    <app-reviewrequest-data [draftId]="id" [Request]="request"
                        [cutterMappingInitial]="request.orD_CutterMap || []"></app-reviewrequest-data>
                </div>
                <kendo-dialog-actions>
                    <button kendoButton (click)="onCancelSubmit()" [disabled]="isSubmitting">No</button>
                    <button kendoButton themeColor="primary" (click)="onSubmit()" [disabled]="isSubmitting">
                        @if(isSubmitting) {
                        <kendo-loader size="small" themeColor="light"></kendo-loader>
                        <span style="margin-left: 8px;">Submitting...</span>
                        } @else {
                        Yes
                        }
                    </button>
                </kendo-dialog-actions>
            </kendo-dialog>
            }
            @if(viewCogs){
            <kendo-dialog (close)="viewCogs=false" [minWidth]="250" [width]="600" title="CoGs"><estimated-cogs
                    class="cogsContainer" [draftId]="id"></estimated-cogs>
            </kendo-dialog>
            }
            @if(viewRejectDialog){
            <kendo-dialog title="Reject Request" (close)="onCancelReject()" [minWidth]="300" [width]="550" class="reject-dialog">
                <div class="reject-dialog-content">
                    <p>Please provide a reason for rejecting this request. The submitter will be notified via email.</p>
                    <kendo-textarea [(ngModel)]="rejectionReason" [rows]="4" placeholder="Enter rejection reason..." style="width: 100%;"></kendo-textarea>
                </div>
                <kendo-dialog-actions>
                    <button kendoButton (click)="onCancelReject()" [disabled]="isRejecting">Cancel</button>
                    <button kendoButton themeColor="error" (click)="onConfirmReject()" [disabled]="isRejecting || !rejectionReason.trim()">
                        @if(isRejecting) {
                        <kendo-loader size="small" themeColor="light"></kendo-loader>
                        <span style="margin-left: 6px;">Rejecting...</span>
                        } @else {
                        Confirm Rejection
                        }
                    </button>
                </kendo-dialog-actions>
            </kendo-dialog>
            }

        </div>
    </div>
</ng-container>

<ng-template #editSkeleton>
    <div class="edit-page">
        <div class="container edit-container">
            <kendo-card class="edit-card skeleton-card" style="width: 100%;">
                <kendo-card-body>
                    <div class="skeleton-header">
                        <kendo-skeleton shape="text" width="180px" height="24px"></kendo-skeleton>
                        <kendo-skeleton shape="text" width="120px" height="18px"></kendo-skeleton>
                    </div>
                    <div class="skeleton-meta">
                        <kendo-skeleton shape="rectangle" width="220px" height="60px"></kendo-skeleton>
                        <kendo-skeleton shape="rectangle" width="220px" height="60px"></kendo-skeleton>
                    </div>
                    <div class="skeleton-tabs">
                        <kendo-skeleton shape="text" width="80px" height="22px"></kendo-skeleton>
                        <kendo-skeleton shape="text" width="80px" height="22px"></kendo-skeleton>
                        <kendo-skeleton shape="text" width="80px" height="22px"></kendo-skeleton>
                        <kendo-skeleton shape="text" width="80px" height="22px"></kendo-skeleton>
                    </div>
                    <div class="skeleton-panel">
                        <kendo-skeleton shape="rectangle" width="100%" height="260px"></kendo-skeleton>
                    </div>
                    <div class="skeleton-panel">
                        <kendo-skeleton shape="rectangle" width="100%" height="260px"></kendo-skeleton>
                    </div>
                </kendo-card-body>
            </kendo-card>
        </div>
    </div>
</ng-template>